import { Play, Lock, AlertCircle, Clock } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';

interface WorkflowCardProps {
  workflow: {
    name: string;
    displayName: string;
    description: string;
    category?: string;
    tags?: string[];
    access: {
      canExecute: boolean;
      requiresApproval: boolean;
      accessLevel: 'unconditional' | 'conditional' | 'view' | 'none';
    };
  };
  onTrigger: () => void;
  onSchedule?: () => void;
}

export function WorkflowCard({ workflow, onTrigger, onSchedule }: WorkflowCardProps) {
  const accessLevelColor = {
    unconditional: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    conditional: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    view: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    none: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
  };

  const accessLevelLabel = {
    unconditional: 'Direct Access',
    conditional: 'Requires Approval',
    view: 'View Only',
    none: 'No Access',
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-gray-300 dark:hover:border-gray-600 transition-colors bg-white dark:bg-gray-800 flex flex-col h-full">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
            {workflow.displayName}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
            {workflow.description || 'No description'}
          </p>
        </div>
        {workflow.access.canExecute ? (
          workflow.access.requiresApproval ? (
            <AlertCircle className="w-5 h-5 text-yellow-500 dark:text-yellow-400 flex-shrink-0 ml-2" />
          ) : (
            <Play className="w-5 h-5 text-green-500 dark:text-green-400 flex-shrink-0 ml-2" />
          )
        ) : (
          <Lock className="w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0 ml-2" />
        )}
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Badge variant="outline" className={accessLevelColor[workflow.access.accessLevel]}>
          {accessLevelLabel[workflow.access.accessLevel]}
        </Badge>
        {workflow.category && (
          <Badge variant="outline" className="text-xs">
            {workflow.category}
          </Badge>
        )}
        {workflow.tags?.map((tag) => (
          <Badge key={tag} variant="outline" className="text-xs bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
            {tag}
          </Badge>
        ))}
      </div>

      <div className="flex gap-2 mt-auto pt-3 border-t border-gray-200 dark:border-gray-700">
        <Button
          onClick={onTrigger}
          disabled={!workflow.access.canExecute}
          className="flex-1"
          variant={workflow.access.canExecute ? 'default' : 'outline'}
        >
          {workflow.access.canExecute ? (
            'Execute Now'
          ) : (
            'Access Denied'
          )}
        </Button>
        {onSchedule && workflow.access.canExecute && (
          <Button
            onClick={onSchedule}
            variant="outline"
            className="px-3"
            title="Schedule workflow"
          >
            <Clock className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}


